import { Injectable } from "@angular/core";
import { CanActivate, ActivatedRouteSnapshot, Router } from "@angular/router";
import { NbToastrService } from "@nebular/theme";
import { PermissionService } from "./permission.service";

/**
 * Guard de rota que verifica permissões do usuário baseado no JWT.
 *
 * Lê a propriedade `data.permission` da configuração da rota e valida
 * se o usuário possui essa permissão no token JWT. Caso não possua,
 * redireciona para o dashboard e exibe um alerta.
 *
 * Suporta permissão única (`data.permission = 'cheque'`) ou múltiplas
 * permissões com lógica OR (`data.permissions = ['cheque', 'deu']`).
 *
 * @example
 * // Uso na configuração de rota:
 * {
 *   path: 'cheque',
 *   canActivate: [AuthGuard, PermissionGuardService],
 *   component: ChequeComponent,
 *   data: { permission: 'cheque' }
 * }
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuardService implements CanActivate {

  constructor(
    private permissionService: PermissionService,
    private router: Router,
    private toastr: NbToastrService
  ) {}

  /**
   * Determina se a rota pode ser ativada com base nas permissões do JWT.
   *
   * @param route - Snapshot da rota sendo acessada, contendo `data.permission` ou `data.permissions`
   * @returns `true` se o usuário tem permissão, `false` caso contrário (redireciona ao dashboard)
   */
  canActivate(route: ActivatedRouteSnapshot): boolean {
    const requiredPermission = route.data["permission"] as string;
    const requiredPermissions = route.data["permissions"] as string[];

    if (!requiredPermission && !requiredPermissions) {
      return true;
    }

    if (requiredPermission && this.permissionService.hasPermission(requiredPermission)) {
      return true;
    }

    if (requiredPermissions && this.permissionService.hasAnyPermission(requiredPermissions)) {
      return true;
    }

    this.toastr.warning(
      "Você não tem permissão para acessar esta tela",
      "Acesso Negado"
    );
    this.router.navigate(["/pages/dashboard"]);
    return false;
  }
}
